import { ApiMethod } from '@wajeez-web/stores/base';
import { Server, contentClient } from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { LibraryItemInput } from './types';

export const getLibraryCount: ApiMethod<
  void,
  Server.LibraryCountOutput
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_libraryCount_get_V1();
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const getMyStartedContent: ApiMethod<
  LibraryItemInput,
  {
    [key: string]: {
      data: Server.LibraryItemOutput[];
      meta: {
        hasNextPage: boolean | undefined;
        totalCount: number | undefined;
        currentPage: number | undefined;
      };
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  const { SortBy, Type, TypeId, PageNumber, PageSize } = data;

  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_myStarted_get_V1(
      undefined,
      SortBy,
      Type,
      TypeId,
      [],
      PageNumber,
      PageSize
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount, currentPage } = response;

      const typeName = Type ? Type : 'all';

      return {
        [typeName]: {
          data: response.data,
          meta: { hasNextPage, totalCount, currentPage },
        },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const getMyCompletedContent: ApiMethod<
  LibraryItemInput,
  {
    [key: string]: {
      data: Server.LibraryItemOutput[];
      meta: {
        hasNextPage: boolean | undefined;
        totalCount: number | undefined;
        currentPage: number | undefined;
      };
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  const { SortBy, Type, TypeId, PageNumber, PageSize } = data;

  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_myCompleted_get_V1(
      undefined,
      SortBy,
      Type,
      TypeId,
      [],
      PageNumber,
      PageSize
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount, currentPage } = response;

      const typeName = Type ? Type : 'all';

      return {
        [typeName]: {
          data: response.data,
          meta: { hasNextPage, totalCount, currentPage },
        },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const getMyFollowedContent: ApiMethod<
  LibraryItemInput,
  {
    [key: string]: {
      data: Server.LibraryItemOutput[];
      meta: {
        hasNextPage: boolean | undefined;
        totalCount: number | undefined;
        currentPage: number | undefined;
      };
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  const { SortBy, Type, TypeId, PageNumber, PageSize } = data;

  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_myFollowed_get_V1(
      undefined,
      SortBy,
      Type,
      TypeId,
      [],
      PageNumber,
      PageSize
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount, currentPage } = response;

      const typeName = Type ? Type : 'all';

      return {
        [typeName]: {
          data: response.data,
          meta: { hasNextPage, totalCount, currentPage },
        },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const getMyBookmarkedContainer: ApiMethod<
  LibraryItemInput,
  {
    data: Server.ContentEntityWithCountOutput[];
    meta: {
      hasNextPage: boolean | undefined;
      totalCount: number | undefined;
      currentPage: number | undefined;
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  const { SortBy, PageNumber, PageSize } = data;

  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_myBookmarkedContainer_get_V1(
      undefined,
      SortBy,
      [],
      PageNumber,
      PageSize
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount, currentPage } = response;

      return {
        data: response.data,
        meta: { hasNextPage, totalCount, currentPage },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const getMyHighlightedContainer: ApiMethod<
  LibraryItemInput,
  {
    data: Server.ContentEntityWithCountOutput[];
    meta: {
      hasNextPage: boolean | undefined;
      totalCount: number | undefined;
      currentPage: number | undefined;
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  const { SortBy, PageNumber, PageSize } = data;

  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_myHighlightedContainer_get_V1(
      undefined,
      SortBy,
      [],
      PageNumber,
      PageSize
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount, currentPage } = response;

      return {
        data: response.data,
        meta: { hasNextPage, totalCount, currentPage },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};
